import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { fireAdGenerated } from "@/lib/pixel";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sliders, Sparkles, ChevronDown, X, Check, Package, Users, Loader2, Upload, Image, Download, Volume2, VolumeX, Trash2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Normalize any error response into a user-readable string.
// Handles Pydantic validation arrays, plain strings, axios errors, and anything else.
function formatError(detail, fallback = "Something went wrong") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        const msg = d?.msg || d?.message || JSON.stringify(d);
        return field ? `${field}: ${msg}` : msg;
      })
      .join("; ");
  }
  if (typeof detail === "object") return detail.msg || detail.message || fallback;
  return String(detail);
}

const VIDEO_TYPES = [
  { value: "custom", label: "Custom", desc: "Your prompt, no style override" },
  { value: "ugc", label: "UGC", desc: "Realistic social media video" },
  { value: "tutorial", label: "Tutorial", desc: "Step-by-step tutorials" },
  { value: "unboxing", label: "Unboxing", desc: "High-quality unboxing" },
  { value: "hyper_motion", label: "Hyper Motion", desc: "Highlight your product" },
  { value: "product_review", label: "Product Review", desc: "Authentic product review" },
  { value: "tv_spot", label: "TV Spot", desc: "Authentic stories, amplified" },
  { value: "wild_card", label: "Wild Card", desc: "A unique and creative video" },
  { value: "ugc_virtual_try_on", label: "UGC Virtual Try On", desc: "Try before you buy" },
  { value: "pro_virtual_try_on", label: "Pro Virtual Try On", desc: "Advanced virtual try-on" },
];

const ASPECT_RATIOS = [
  { value: "16:9", label: "16:9", icon: "▬" },
  { value: "9:16", label: "9:16", icon: "▮" },
  { value: "1:1", label: "1:1", icon: "■" },
];

const RESOLUTIONS = [];

export default function MarketingStudio() {
  const [description, setDescription] = useState("");
  const [videoType, setVideoType] = useState("custom");
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [duration, setDuration] = useState(8);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [shotMode, setShotMode] = useState("single"); // "single" | "multi"
  const isDirectorMode = videoType !== "custom"; // AI Director auto-engaged

  // Auto-engage multi-shot when AI Director is active; reset to single when user returns to custom (per design decision: clean slate)
  useEffect(() => {
    if (isDirectorMode) {
      setShotMode("multi");
    } else {
      setShotMode("single");
    }
  }, [isDirectorMode]);
  const [startFrame, setStartFrame] = useState(null); // { file, preview } or { url, preview }
  const [endFrame, setEndFrame] = useState(null);
  const startFrameRef = useRef();
  const endFrameRef = useRef();
  const [showStartFrameMenu, setShowStartFrameMenu] = useState(false);
  const [showEndFrameMenu, setShowEndFrameMenu] = useState(false);
  const [showPhotoshootPicker, setShowPhotoshootPicker] = useState(null); // "start" | "end" | null

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null); // { status, progress, message, video_url }
  const [costPerSecond, setCostPerSecond] = useState(2);
  const [videoHistory, setVideoHistory] = useState([]);
  const [videoModal, setVideoModal] = useState(null); // job object to show in modal

  // Fetch pricing + history on mount
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [pricingRes, historyRes] = await Promise.all([
          axios.get(`${API}/api/marketing-studio/pricing`, { withCredentials: true }),
          axios.get(`${API}/api/marketing-studio/history`, { withCredentials: true }),
        ]);
        setCostPerSecond(pricingRes.data.cost_per_second || 2);

        const jobs = historyRes.data.jobs || [];
        setVideoHistory(jobs);

        // Resume polling if latest job is still processing and not stale (< 15 min)
        const processingJob = jobs.find((j) => j.status === "processing");
        if (processingJob) {
          const jobAge = (Date.now() - new Date(processingJob.created_at).getTime()) / 1000 / 60;
          if (jobAge < 15) {
            setJobId(processingJob.job_id);
            setJobStatus(processingJob);
            if (processingJob.aspect_ratio) setAspectRatio(processingJob.aspect_ratio);
            setGenerating(true);
            pollJob(processingJob.job_id);
          } else {
            // Stale job — treat as failed locally
            setJobStatus({ ...processingJob, status: "failed", error: "Generation timed out. Please try again." });
          }
        }
      } catch { /* use defaults */ }
    };
    fetchInitial();
  }, []);

  const totalCost = costPerSecond * duration;

  const currentType = VIDEO_TYPES.find((t) => t.value === videoType);
  const settingsRef = useRef();
  const typeDropdownRef = useRef();
  const startFrameMenuRef = useRef();
  const endFrameMenuRef = useRef();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSettings && settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
      if (showTypeDropdown && typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
        setShowTypeDropdown(false);
      }
      if (showStartFrameMenu && startFrameMenuRef.current && !startFrameMenuRef.current.contains(e.target)) {
        setShowStartFrameMenu(false);
      }
      if (showEndFrameMenu && endFrameMenuRef.current && !endFrameMenuRef.current.contains(e.target)) {
        setShowEndFrameMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSettings, showTypeDropdown, showStartFrameMenu, showEndFrameMenu]);

  const handleFrameUpload = (file, setter) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setter({ file, preview: e.target.result });
    reader.readAsDataURL(file);
  };

  // Generate video
  const productNeedsMoreImages =
    selectedProduct && (selectedProduct.images || []).length < 2;
  const multiShotEndFrameConflict = shotMode === "multi" && !!endFrame;
  // Alert (non-blocking) when prompt references @product or @avatar but that element isn't selected
  const promptLower = (description || "").toLowerCase();
  const danglingProductRef = !selectedProduct && /@product\b/i.test(promptLower);
  const danglingAvatarRef = !selectedAvatar && /@avatar\b/i.test(promptLower);
  const blocksGeneration = productNeedsMoreImages || multiShotEndFrameConflict;

  const handleGenerate = async () => {
    const promptEmpty = !description.trim();
    // Custom requires a description; non-custom (AI Director) requires either a description OR product/avatar
    if (promptEmpty) {
      if (videoType === "custom") return toast.error("Please describe your video ad");
      if (!selectedProduct && !selectedAvatar) return toast.error("Add a product, avatar, or describe your ad");
    } else if (!selectedProduct && !selectedAvatar) {
      return toast.error("Select at least a product or avatar");
    }
    if (productNeedsMoreImages) {
      return toast.error(
        "Selected product has only 1 image. At least 2 images are required. Please delete it in Product Studio and recreate with more images."
      );
    }
    if (shotMode === "multi" && endFrame) {
      return toast.error(
        "End frame can't be used with Multi-Shot mode. Please disable Multi-Shot or clear the end frame."
      );
    }

    setGenerating(true);
    setJobStatus({ status: "processing", progress: 5, message: "Starting..." });

    try {
      const formData = new FormData();
      formData.append("prompt", description.trim());
      formData.append("aspect_ratio", aspectRatio);
      formData.append("duration", String(duration));
      formData.append("shot_type", shotMode === "multi" ? "intelligent" : "single_shot");
      formData.append("video_type", videoType);
      if (selectedProduct) formData.append("product_id", selectedProduct.id);
      if (selectedAvatar) formData.append("avatar_id", selectedAvatar.id);
      if (startFrame?.file) formData.append("start_frame", startFrame.file);
      else if (startFrame?.url) formData.append("start_frame_url", startFrame.url);
      if (endFrame?.file) formData.append("end_frame", endFrame.file);
      else if (endFrame?.url) formData.append("end_frame_url", endFrame.url);

      const res = await axios.post(`${API}/api/marketing-studio/generate`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { job_id } = res.data;
      setJobId(job_id);

      // Start polling
      pollJob(job_id);
    } catch (err) {
      setGenerating(false);
      setJobStatus(null);
      toast.error(formatError(err.response?.data?.detail, "Failed to start generation"));
    }
  };

  const pollJob = async (jid) => {
    let attempts = 0;
    const maxAttempts = 180; // 15 min max
    while (attempts < maxAttempts) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const res = await axios.get(`${API}/api/marketing-studio/job/${jid}`, { withCredentials: true });
        const job = res.data;
        setJobStatus(job);
        if (job.status === "completed") {
          setGenerating(false);
          toast.success("Video generated!");
          fireAdGenerated();
          // Refresh history
          try {
            const hRes = await axios.get(`${API}/api/marketing-studio/history`, { withCredentials: true });
            setVideoHistory(hRes.data.jobs || []);
          } catch {}
          return;
        } else if (job.status === "failed") {
          setGenerating(false);
          toast.error(formatError(job.error, "Generation failed"));
          return;
        }
      } catch {
        // Network error, keep polling
      }
      attempts++;
    }
    setGenerating(false);
    toast.error("Generation timed out");
  };

  const handleDownload = (videoUrl) => {
    const downloadUrl = `${API}/api/download-video?url=${encodeURIComponent(videoUrl)}`;
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = videoUrl.split("/").pop()?.split("?")[0] || "video.mp4";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.info("Downloading video...");
  };

  const handleDeleteVideo = async (jobId) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await axios.delete(`${API}/api/marketing-studio/job/${jobId}`, { withCredentials: true });
      toast.success("Video deleted");
      setVideoHistory((prev) => prev.filter((j) => j.job_id !== jobId));
      if (videoModal?.job_id === jobId) setVideoModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const getCardWidth = (ar) => ar === "9:16" ? "w-[180px]" : ar === "16:9" ? "w-[570px]" : "w-[320px]";

  const aspectClass = aspectRatio === "9:16" ? "max-w-xs aspect-[9/16]" : aspectRatio === "16:9" ? "max-w-2xl aspect-video" : "max-w-sm aspect-square";

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden" data-testid="marketing-studio-page">
      {/* Background gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[hsl(11,100%,61%)] opacity-[0.08] blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 pt-16 pb-24">
        {/* Hero */}
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-primary font-semibold mb-4" data-testid="ms-subtitle">
            Marketing Studio
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase leading-[1.1] tracking-tight">
            Turn Any Product<br />Into a Video Ad
          </h1>
        </div>

        {/* Main Creation Card */}
        <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-5 space-y-4" data-testid="ms-creation-card">
          {/* Row 1: Description */}
          <div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={isDirectorMode
                ? "Description is optional — we'll compose from your selections.\nOr add specifics like setting, mood, must-include elements..."
                : "Describe what happens in the ad...\nUse @product and @avatar to reference your selections"}
              className="w-full bg-transparent text-white placeholder-white/30 text-base resize-none outline-none min-h-[60px]"
              rows={2}
              data-testid="ms-description-input"
            />
            {isDirectorMode && !description.trim() && (selectedProduct || selectedAvatar) && (
              <p className="text-xs text-primary/70 mt-1 flex items-center gap-1" data-testid="ms-director-hint">
                <Sparkles className="w-3 h-3" /> AI Director will compose the prompt from your selections.
              </p>
            )}
          </div>

          {/* Row 2: Center controls */}
          <div className="flex items-center justify-center gap-3">
            {/* Video Type Dropdown */}
            <div className="relative z-[55]" ref={typeDropdownRef}>
              <button
                onClick={() => { setShowTypeDropdown(!showTypeDropdown); setShowSettings(false); }}
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 text-white text-sm hover:bg-white/15 transition-colors"
                data-testid="ms-type-dropdown-trigger"
              >
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                {currentType?.label || "Select Type"}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showTypeDropdown ? "rotate-180" : ""}`} />
              </button>
              {showTypeDropdown && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-slate-800 border border-white/10 rounded-xl shadow-2xl z-[60] py-1 max-h-80 overflow-y-auto" data-testid="ms-type-dropdown" onClick={(e) => e.stopPropagation()}>
                  {VIDEO_TYPES.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => { setVideoType(type.value); setShowTypeDropdown(false); }}
                      className={`w-full text-left px-4 py-2.5 flex flex-col hover:bg-white/5 transition-colors ${videoType === type.value ? "bg-primary/10" : ""}`}
                      data-testid={`ms-type-option-${type.value}`}
                    >
                      <span className={`text-sm font-medium ${videoType === type.value ? "text-primary" : "text-white"}`}>{type.label}</span>
                      <span className="text-[11px] text-white/40">{type.desc}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Shot Mode Toggle */}
            <div
              className={`flex rounded-full bg-white/10 p-0.5 ${isDirectorMode ? "opacity-60" : ""}`}
              data-testid="ms-shot-mode-toggle"
              title={isDirectorMode ? "Multi-Shot is required for AI Director video types. Switch to Custom to unlock." : ""}
            >
              <button
                onClick={() => !isDirectorMode && setShotMode("single")}
                disabled={isDirectorMode}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  shotMode === "single" ? "bg-primary/20 text-primary" : "text-white/50 hover:text-white/70"
                } ${isDirectorMode ? "cursor-not-allowed" : ""}`}
                data-testid="ms-shot-single"
              >
                Single Shot
              </button>
              <button
                onClick={() => !isDirectorMode && setShotMode("multi")}
                disabled={isDirectorMode}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  shotMode === "multi" ? "bg-primary/20 text-primary" : "text-white/50 hover:text-white/70"
                } ${isDirectorMode ? "cursor-not-allowed" : ""}`}
                data-testid="ms-shot-multi"
              >
                Multi-Shot{isDirectorMode && <span className="ml-1 text-[9px] text-primary/70">AUTO</span>}
              </button>
            </div>

            {/* Settings Toggle */}
            <div className="relative z-[55]" ref={settingsRef}>
              <button
                onClick={() => { setShowSettings(!showSettings); setShowTypeDropdown(false); }}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${showSettings ? "bg-primary/20 text-primary" : "bg-white/10 text-white/60 hover:bg-white/15"}`}
                data-testid="ms-settings-trigger"
              >
                <Sliders className="w-4 h-4" />
              </button>
              {showSettings && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-slate-800 border border-white/10 rounded-xl shadow-2xl z-[60] p-4 space-y-5" data-testid="ms-settings-panel" onClick={(e) => e.stopPropagation()}>
                  {/* Aspect Ratio */}
                  <div>
                    <p className="text-xs text-white/40 font-medium mb-2">Aspect Ratio</p>
                    <div className="flex gap-2">
                      {ASPECT_RATIOS.map((ar) => (
                        <button
                          key={ar.value}
                          onClick={(e) => { e.stopPropagation(); setAspectRatio(ar.value); }}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            aspectRatio === ar.value
                              ? "bg-primary/20 text-primary border border-primary/30"
                              : "bg-white/5 text-white/60 border border-white/5 hover:bg-white/10"
                          }`}
                          data-testid={`ms-ar-${ar.value.replace(":", "-")}`}
                        >
                          <span className="text-[10px]">{ar.icon}</span>
                          {ar.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Duration Slider */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-white/40 font-medium">Duration</p>
                      <span className="text-xs text-primary font-semibold">{duration}s</span>
                    </div>
                    <input
                      type="range"
                      min="4"
                      max="15"
                      value={duration}
                      onChange={(e) => setDuration(Number(e.target.value))}
                      className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-white/10 accent-primary"
                      style={{ accentColor: 'hsl(11, 100%, 61%)' }}
                      data-testid="ms-duration-slider"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-[10px] text-white/30">4s</span>
                      <span className="text-[10px] text-white/30">15s</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Row 3: Cards + Generate */}
          <div className="flex items-center justify-center gap-3">
            {/* Start Frame */}
            <input ref={startFrameRef} type="file" accept="image/*" className="hidden" onChange={(e) => { handleFrameUpload(e.target.files[0], setStartFrame); setShowStartFrameMenu(false); }} />
            <div className="relative" ref={startFrameMenuRef}>
              {startFrame && (
                <button
                  onClick={(e) => { e.stopPropagation(); setStartFrame(null); setShowStartFrameMenu(false); }}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 border border-white/20 text-white/70 hover:bg-red-600/80 hover:text-white hover:border-red-500 flex items-center justify-center z-10 transition-colors shadow-md"
                  data-testid="ms-start-frame-clear"
                  aria-label="Clear start frame"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => { setShowStartFrameMenu(!showStartFrameMenu); setShowEndFrameMenu(false); }}
                className="w-[72px] h-[88px] rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors flex flex-col items-center justify-center gap-1.5 overflow-hidden"
                data-testid="ms-start-frame-trigger"
              >
                {startFrame ? (
                  <>
                    <img src={startFrame.preview || startFrame.url} alt="Start frame" className="w-full h-14 object-cover rounded-t-lg" />
                    <span className="text-[9px] text-white/70 truncate w-full text-center px-1">Start</span>
                  </>
                ) : (
                  <>
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                      <Image className="w-4 h-4 text-white/40" />
                    </div>
                    <span className="text-[9px] text-white/40 font-medium uppercase tracking-wide leading-tight text-center">Start<br />Frame</span>
                  </>
                )}
              </button>
              {showStartFrameMenu && (
                <div className="absolute bottom-full left-0 mb-2 w-40 bg-slate-800 border border-white/10 rounded-xl shadow-2xl z-[60] py-1" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => { startFrameRef.current?.click(); }} className="w-full text-left px-3 py-2 text-xs text-white/70 hover:bg-white/5 flex items-center gap-2" data-testid="start-frame-upload">
                    <Upload className="w-3.5 h-3.5" />Upload Image
                  </button>
                  <button onClick={() => { setShowPhotoshootPicker("start"); setShowStartFrameMenu(false); }} className="w-full text-left px-3 py-2 text-xs text-white/70 hover:bg-white/5 flex items-center gap-2" data-testid="start-frame-photoshoot">
                    <Image className="w-3.5 h-3.5" />From Photoshoot
                  </button>
                </div>
              )}
            </div>

            {/* End Frame */}
            <input ref={endFrameRef} type="file" accept="image/*" className="hidden" onChange={(e) => { handleFrameUpload(e.target.files[0], setEndFrame); setShowEndFrameMenu(false); }} />
            <div className="relative" ref={endFrameMenuRef}>
              {endFrame && (
                <button
                  onClick={(e) => { e.stopPropagation(); setEndFrame(null); setShowEndFrameMenu(false); }}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 border border-white/20 text-white/70 hover:bg-red-600/80 hover:text-white hover:border-red-500 flex items-center justify-center z-10 transition-colors shadow-md"
                  data-testid="ms-end-frame-clear"
                  aria-label="Clear end frame"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => { setShowEndFrameMenu(!showEndFrameMenu); setShowStartFrameMenu(false); }}
                className="w-[72px] h-[88px] rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors flex flex-col items-center justify-center gap-1.5 overflow-hidden"
                data-testid="ms-end-frame-trigger"
              >
                {endFrame ? (
                  <>
                    <img src={endFrame.preview || endFrame.url} alt="End frame" className="w-full h-14 object-cover rounded-t-lg" />
                    <span className="text-[9px] text-white/70 truncate w-full text-center px-1">End</span>
                  </>
                ) : (
                  <>
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                      <Image className="w-4 h-4 text-white/40" />
                    </div>
                    <span className="text-[9px] text-white/40 font-medium uppercase tracking-wide leading-tight text-center">End<br />Frame</span>
                  </>
                )}
              </button>
              {showEndFrameMenu && (
                <div className="absolute bottom-full left-0 mb-2 w-40 bg-slate-800 border border-white/10 rounded-xl shadow-2xl z-[60] py-1" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => { endFrameRef.current?.click(); }} className="w-full text-left px-3 py-2 text-xs text-white/70 hover:bg-white/5 flex items-center gap-2" data-testid="end-frame-upload">
                    <Upload className="w-3.5 h-3.5" />Upload Image
                  </button>
                  <button onClick={() => { setShowPhotoshootPicker("end"); setShowEndFrameMenu(false); }} className="w-full text-left px-3 py-2 text-xs text-white/70 hover:bg-white/5 flex items-center gap-2" data-testid="end-frame-photoshoot">
                    <Image className="w-3.5 h-3.5" />From Photoshoot
                  </button>
                </div>
              )}
            </div>

            {/* Product Picker */}
            <div className="relative">
              {selectedProduct && (
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedProduct(null); }}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 border border-white/20 text-white/70 hover:bg-red-600/80 hover:text-white hover:border-red-500 flex items-center justify-center z-10 transition-colors shadow-md"
                  data-testid="ms-product-clear"
                  aria-label="Clear product"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => setShowProductPicker(true)}
                className="w-[72px] h-[88px] rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors flex flex-col items-center justify-center gap-1.5 overflow-hidden"
                data-testid="ms-product-picker-trigger"
              >
                {selectedProduct ? (
                  <>
                    <img src={(selectedProduct.images || [])[0]} alt={selectedProduct.name} className="w-full h-14 object-cover rounded-t-lg" />
                    <span className="text-[9px] text-white/70 truncate w-full text-center px-1">{selectedProduct.name}</span>
                  </>
                ) : (
                  <>
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                      <Package className="w-4 h-4 text-white/40" />
                    </div>
                    <span className="text-[10px] text-white/40 font-medium uppercase tracking-wide">Product</span>
                  </>
                )}
              </button>
            </div>

            {/* Avatar Picker */}
            <div className="relative">
              {selectedAvatar && (
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedAvatar(null); }}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 border border-white/20 text-white/70 hover:bg-red-600/80 hover:text-white hover:border-red-500 flex items-center justify-center z-10 transition-colors shadow-md"
                  data-testid="ms-avatar-clear"
                  aria-label="Clear avatar"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => setShowAvatarPicker(true)}
                className="w-[72px] h-[88px] rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors flex flex-col items-center justify-center gap-1.5 overflow-hidden"
                data-testid="ms-avatar-picker-trigger"
              >
                {selectedAvatar ? (
                  <>
                    <img src={(selectedAvatar.images || [])[0]} alt={selectedAvatar.name} className="w-full h-14 object-cover rounded-t-lg" />
                    <span className="text-[9px] text-white/70 truncate w-full text-center px-1">{selectedAvatar.name}</span>
                  </>
                ) : (
                  <>
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                      <Users className="w-4 h-4 text-white/40" />
                    </div>
                    <span className="text-[10px] text-white/40 font-medium uppercase tracking-wide">Avatar</span>
                  </>
                )}
              </button>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={generating || blocksGeneration}
              title={productNeedsMoreImages ? "Selected product needs at least 2 images" : multiShotEndFrameConflict ? "End frame not supported with Multi-Shot" : undefined}
              className={`h-[88px] px-6 rounded-xl bg-gradient-to-br from-primary to-[hsl(11,100%,50%)] text-white font-bold text-sm uppercase tracking-wider flex flex-col items-center justify-center gap-1 transition-opacity shadow-lg shadow-primary/20 ${generating || blocksGeneration ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
              data-testid="ms-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-[10px] font-medium">Processing</span>
                </>
              ) : (
                <>
                  <span className="text-xs font-black tracking-widest">Generate</span>
                  <span className="text-[10px] font-medium opacity-80">{totalCost} credits</span>
                </>
              )}
            </button>
          </div>

          {/* Product image warning — blocks generation */}
          {productNeedsMoreImages && (
            <div
              className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs"
              data-testid="ms-product-image-warning"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" x2="12" y1="9" y2="13"/><line x1="12" x2="12.01" y1="17" y2="17"/></svg>
              <span>
                Selected product has only 1 image. At least 2 images are required to generate a video. Please delete it in Product Studio and recreate it with more images.
              </span>
            </div>
          )}

          {/* Multi-Shot + End Frame conflict warning — Fal.ai constraint */}
          {shotMode === "multi" && endFrame && (
            <div
              className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs"
              data-testid="ms-multishot-endframe-warning"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" x2="12" y1="9" y2="13"/><line x1="12" x2="12.01" y1="17" y2="17"/></svg>
              <span>
                End frame can't be used with Multi-Shot mode. Please disable Multi-Shot or clear the end frame.
              </span>
            </div>
          )}

          {/* Dangling @reference alert — non-blocking */}
          {(danglingProductRef || danglingAvatarRef) && (
            <div
              className="flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs"
              data-testid="ms-dangling-reference-warning"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>
              <span>
                {danglingProductRef && danglingAvatarRef
                  ? "Your prompt mentions @product and @avatar but neither is selected. These references will be removed before generation."
                  : danglingProductRef
                  ? "Your prompt mentions @product but no product is selected. The @product reference will be removed."
                  : "Your prompt mentions @avatar but no avatar is selected. The @avatar reference will be removed."}
              </span>
            </div>
          )}
        </div>

        {/* Video Grid — Processing card + history */}
        {(generating || videoHistory.filter((j) => j.status === "completed" || j.status === "failed").length > 0) && (
          <div className="mt-10">
            <h2 className="text-sm font-semibold text-white/50 uppercase tracking-widest mb-4">Your Videos</h2>
            <div className="flex flex-wrap gap-4 justify-center">
              {/* Processing card as first grid item */}
              {generating && jobStatus && jobStatus.status === "processing" && (
                <div className={`${getCardWidth(aspectRatio)} h-80 rounded-2xl overflow-hidden border border-primary/20 bg-slate-900/80 relative shrink-0`} data-testid="ms-result-card">
                  <div className="h-full relative">
                    <div className="absolute top-0 left-0 right-0 h-24 overflow-hidden">
                      <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-[200%] h-24 rounded-[50%] bg-white/[0.03]" />
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-[180%] h-20 rounded-[50%] bg-white/[0.02]" />
                    </div>
                    <div className="relative p-4">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
                        <Loader2 className="w-3.5 h-3.5 text-white/70 animate-spin" />
                        <span className="text-xs text-white/70 font-medium">Processing</span>
                      </div>
                    </div>
                    {jobStatus.progress > 0 && (
                      <div className="absolute bottom-0 left-0 right-0 px-4 pb-4">
                        <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-primary/50 rounded-full transition-all duration-1000" style={{ width: `${jobStatus.progress}%` }} />
                        </div>
                        <p className="text-[10px] text-white/30 mt-1.5 text-center">{jobStatus.message || "Generating..."}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Completed / Failed cards */}
              {videoHistory.filter((j) => j.status === "completed" || j.status === "failed").map((job) => (
                <VideoHistoryCard
                  key={job.job_id}
                  job={job}
                  getCardWidth={getCardWidth}
                  onDownload={handleDownload}
                  onOpen={setVideoModal}
                  onDelete={handleDeleteVideo}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Video Modal */}
      {videoModal && (
        <VideoModal job={videoModal} onClose={() => setVideoModal(null)} onDownload={handleDownload} />
      )}

      {/* Product Picker Modal */}
      {showProductPicker && (
        <PickerModal
          title="Select Product"
          icon={<Package className="w-5 h-5" />}
          endpoint={`${API}/api/products`}
          dataKey="products"
          onSelect={(item) => { setSelectedProduct(item); setShowProductPicker(false); }}
          onClose={() => setShowProductPicker(false)}
          emptyMessage="No products yet. Add products in Product Studio first."
          selected={selectedProduct}
        />
      )}

      {/* Avatar Picker Modal */}
      {showAvatarPicker && (
        <PickerModal
          title="Select Avatar"
          icon={<Users className="w-5 h-5" />}
          endpoint={`${API}/api/avatars`}
          dataKey="custom"
          secondaryKey="presets"
          onSelect={(item) => { setSelectedAvatar(item); setShowAvatarPicker(false); }}
          onClose={() => setShowAvatarPicker(false)}
          emptyMessage="No avatars yet. Add avatars in Avatar Studio first."
          selected={selectedAvatar}
        />
      )}

      {/* Photoshoot Picker Modal */}
      {showPhotoshootPicker && (
        <PhotoshootPickerModal
          onSelect={(url) => {
            const setter = showPhotoshootPicker === "start" ? setStartFrame : setEndFrame;
            setter({ url, preview: url });
            setShowPhotoshootPicker(null);
          }}
          onClose={() => setShowPhotoshootPicker(null)}
        />
      )}
    </div>
  );
}


function VideoHistoryCard({ job, getCardWidth, onDownload, onOpen, onDelete }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  if (job.status === "failed") {
    return (
      <div className="w-[180px] h-80 rounded-2xl overflow-hidden border border-white/10 bg-slate-900/60 shrink-0" data-testid={`history-card-${job.job_id}`}>
        <div className="h-full flex flex-col items-center justify-center gap-2 p-4">
          <p className="text-xs text-red-400 text-center">{formatError(job.error, "Failed")}</p>
          <p className="text-[10px] text-white/30">{new Date(job.created_at).toLocaleDateString()}</p>
        </div>
      </div>
    );
  }

  if (!job.video_url) return null;

  return (
    <div
      className={`${getCardWidth(job.aspect_ratio || "9:16")} h-80 rounded-2xl overflow-hidden border border-white/10 bg-slate-900/60 relative group cursor-pointer shrink-0`}
      data-testid={`history-card-${job.job_id}`}
      onClick={() => onOpen(job)}
    >
      <div className="h-full bg-black">
        <video
          ref={videoRef}
          src={job.video_url}
          className="w-full h-full object-cover"
          muted={muted}
          playsInline
          onMouseEnter={(e) => e.target.play().catch(() => {})}
          onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
        />
      </div>
      {/* Download button */}
      <button
        onClick={(e) => { e.stopPropagation(); onDownload(job.video_url); }}
        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary/80"
        data-testid={`download-${job.job_id}`}
      >
        <Download className="w-4 h-4" />
      </button>
      {/* Delete button */}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(job.job_id); }}
        className="absolute top-2 left-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600/80"
        data-testid={`delete-video-${job.job_id}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
      {/* Mute/Unmute button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setMuted(!muted);
          if (videoRef.current) videoRef.current.muted = !muted;
        }}
        className="absolute bottom-8 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/20"
        data-testid={`mute-${job.job_id}`}
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
        <p className="text-[10px] text-white/50">{job.aspect_ratio} &middot; {job.duration}s &middot; {new Date(job.created_at).toLocaleDateString()}</p>
      </div>
    </div>
  );
}


function VideoModal({ job, onClose, onDownload }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="video-modal">
      <div className="relative max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors" data-testid="video-modal-close">
          <X className="w-6 h-6" />
        </button>

        {/* Video */}
        <div className="rounded-2xl overflow-hidden bg-black border border-white/10 flex-1 min-h-0">
          <video
            src={job.video_url}
            controls
            autoPlay
            className="w-full max-h-[65vh] object-contain"
            data-testid="video-modal-player"
          />
        </div>

        {/* Meta + Download */}
        <div className="flex items-center justify-between mt-4">
          <div>
            <p className="text-sm text-white/60">{job.aspect_ratio} &middot; {job.duration}s</p>
            <p className="text-xs text-white/30 mt-0.5">{new Date(job.created_at).toLocaleString()}</p>
            {job.prompt && <p className="text-xs text-white/20 mt-1 line-clamp-2 max-w-lg">{job.prompt}</p>}
          </div>
          <button
            onClick={() => onDownload(job.video_url)}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary text-xs font-medium hover:bg-primary/30 transition-colors"
            data-testid="video-modal-download"
          >
            <Download className="w-4 h-4" />Download
          </button>
        </div>
      </div>
    </div>
  );
}


function PhotoshootPickerModal({ onSelect, onClose }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const res = await axios.get(`${API}/api/photoshoot/history?limit=50`, { withCredentials: true });
        const shoots = (res.data.photoshoots || []).filter((s) => s.generated_image_url);
        setPhotos(shoots);
      } catch {
        toast.error("Failed to load photoshoots");
      } finally {
        setLoading(false);
      }
    };
    fetchPhotos();
  }, []);

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="photoshoot-picker-modal">
      <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2 text-white">
            <Image className="w-5 h-5" />
            <h3 className="font-semibold text-sm">Select from Photoshoot</h3>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-white/40" />
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-white/40 text-sm">No photoshoot images found. Generate some in Photoshoot first.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {photos.map((photo) => (
                <button
                  key={photo.id}
                  onClick={() => onSelect(photo.generated_image_url)}
                  className="relative rounded-xl overflow-hidden border-2 border-white/10 hover:border-primary/50 transition-all"
                  data-testid={`photoshoot-pick-${photo.id}`}
                >
                  <div className="aspect-square bg-slate-800">
                    <img src={photo.generated_image_url} alt={photo.prompt || "Photoshoot"} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function PickerModal({ title, icon, endpoint, dataKey, secondaryKey, onSelect, onClose, emptyMessage, selected }) {
  const [items, setItems] = useState([]);
  const [secondaryItems, setSecondaryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await axios.get(endpoint, { withCredentials: true });
        setItems(res.data[dataKey] || []);
        if (secondaryKey) setSecondaryItems(res.data[secondaryKey] || []);
      } catch {
        toast.error(`Failed to load ${title.toLowerCase()}`);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [endpoint, dataKey, secondaryKey, title]);

  const allItems = [...items, ...secondaryItems];

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="picker-modal">
      <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2 text-white">
            {icon}
            <h3 className="font-semibold text-sm">{title}</h3>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-white/40" />
            </div>
          ) : allItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-white/40 text-sm">{emptyMessage}</p>
            </div>
          ) : (
            <>
              {secondaryItems.length > 0 && items.length > 0 && (
                <p className="text-[10px] uppercase tracking-widest text-white/30 font-semibold mb-2 px-1">My {title}s</p>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {items.map((item) => (
                  <PickerItem key={item.id} item={item} isSelected={selected?.id === item.id} onSelect={onSelect} />
                ))}
              </div>

              {secondaryItems.length > 0 && (
                <>
                  <p className="text-[10px] uppercase tracking-widest text-white/30 font-semibold mt-5 mb-2 px-1">Presets</p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {secondaryItems.map((item) => (
                      <PickerItem key={item.id} item={item} isSelected={selected?.id === item.id} onSelect={onSelect} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function PickerItem({ item, isSelected, onSelect }) {
  const heroImage = (item.images || [])[0];
  return (
    <button
      onClick={() => onSelect(item)}
      className={`relative rounded-xl overflow-hidden border-2 transition-all ${
        isSelected
          ? "border-primary ring-2 ring-primary/30 scale-[1.02]"
          : "border-white/10 hover:border-white/25"
      }`}
      data-testid={`picker-item-${item.id}`}
    >
      <div className="aspect-square bg-slate-800">
        {heroImage ? (
          <img src={heroImage} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            <Package className="w-6 h-6" />
          </div>
        )}
      </div>
      <div className="px-1.5 py-1.5 bg-slate-800/90">
        <p className="text-[10px] text-white/70 font-medium truncate text-center">{item.name}</p>
      </div>
      {isSelected && (
        <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
          <Check className="w-3 h-3 text-white" />
        </div>
      )}
    </button>
  );
}
